
import { create } from "zustand";
import { fetchHoldings, fetchPositions } from "../services/kiteService";

interface PortfolioStats {
  totalInvestment: number;
  currentValue: number;
  overallPNL: number;
  todayPNL: number;
  overallPNLPercent: number;
}

interface PortfolioState {
  holdings: any[];
  positions: any[];
  stats: PortfolioStats;
  loading: boolean;
  isInitialized: boolean;
  fetchPortfolio: () => Promise<void>;
  updateStats: (prices: Record<number, { price: number; ohlcClose?: number; change?: number }>) => void;
}

export const usePortfolioStore = create<PortfolioState>((set, get) => ({
  holdings: [],
  positions: [],
  stats: {
    totalInvestment: 0,
    currentValue: 0,
    overallPNL: 0,
    todayPNL: 0,
    overallPNLPercent: 0,
  },
  loading: false,
  isInitialized: false,

  fetchPortfolio: async () => {
    set({ loading: true });
    try {
      const [holdingsData, positionsResp] = await Promise.all([
        fetchHoldings(),
        fetchPositions(),
      ]);
      const holdings = holdingsData || [];
      const positions = positionsResp?.net || [];
      
      set({ holdings, positions, loading: false, isInitialized: true });
      // Initial stats calculation (using last_price from Kite)
      get().updateStats({});
    } catch (error) {
      console.error("Error fetching portfolio in store:", error);
      set({ loading: false });
    }
  },

  updateStats: (prices) => {
    const { holdings, positions } = get();
    let totalInvestment = 0;
    let currentValue = 0;
    let overallPNL = 0;
    let todayPNL = 0;

    holdings.forEach((h) => {
      const livePrice = prices[h.instrument_token]?.price || h.last_price;
      const totalQty = h.quantity + (h.t1_quantity || 0);
      
      totalInvestment += h.quantity * h.average_price;
      currentValue += totalQty * livePrice;
      
      // Overall P&L: (LTP - Avg Price) * Total Qty
      overallPNL += (livePrice - h.average_price) * totalQty;
      
      // Today P&L: (LTP - Prev Close) * Total Qty
      todayPNL += (livePrice - h.close_price) * totalQty;
    });

    positions.forEach((p) => {
      // Kite positions provide day_pnl and pnl (unrealised + realised)
      todayPNL += p.day_pnl || 0;
      overallPNL += p.pnl || 0;
      
      // For positions, they don't count towards "Total Investment" in the same way CNC holdings do
      // but they contribute to current value and P&L.
      currentValue += p.pnl || 0; 
    });

    const overallPNLPercent = totalInvestment > 0 ? (overallPNL / totalInvestment) * 100 : 0;

    set({
      stats: {
        totalInvestment,
        currentValue,
        overallPNL,
        todayPNL,
        overallPNLPercent,
      },
    });
  },
}));
